import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { getSettingsRequest } from "../../../redux/settings/settingsSlice";
import { bulkGiftRequest } from "../../../redux/settings/settingsSlice";
import { getMetalsRequest } from "../../../redux/GetMetals/metalSlice";
import { getAppConfig } from "../../../redux/Configuration/configurationSlice";
import FeatureUnavailable from "../../Comman/FeatureUnavailable";
import { canShowPermission } from "../Permissions/permissionObject";

const BulkGifts = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const metalsdata = data?.data?.metals || [];
  const configData = useSelector((state) => state.appConfig.configData);

  const [selectedMetal, setSelectedMetal] = useState(null);
  const [occasion, setOccasion] = useState("");
  const [giftValidity, setGiftValidity] = useState("");
  const [weight, setWeight] = useState("");
  const [mobile, setMobile] = useState("");
  const [mobileList, setMobileList] = useState([]);
  const [bulkMetalGiftFile, setBulkMetalGiftFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreateSetting = canShowPermission(permissions, "bulkGifts", "create");
  const canEdit = canShowPermission(permissions, "bulkGifts", "edit");

  useEffect(() => {
    dispatch(getSettingsRequest());
    dispatch(getMetalsRequest());
    // dispatch(getAppConfig());
  }, [dispatch]);

  const MIN_PHONE_LENGTH = 5;
  const MAX_PHONE_LENGTH = 13;

  const handleRemoveMobile = (number) => {
    setMobileList((prev) => prev.filter((n) => n !== number));
  };

  const handleAddMobile = () => {
    const cleanedMobile = mobile.replace(/\D/g, "").replace(/^0+/, "");
    if (
      cleanedMobile.length >= MIN_PHONE_LENGTH &&
      cleanedMobile.length <= MAX_PHONE_LENGTH &&
      !mobileList.includes(cleanedMobile)
    ) {
      setMobileList((prev) => [...prev, cleanedMobile]);
      setMobile("");
    } else {
      toast.error(
        `Mobile number must be between ${MIN_PHONE_LENGTH} and ${MAX_PHONE_LENGTH} digits`
      );
    }
  };

  const handleBulkGift = () => {
    if (!mobileList.length && !bulkMetalGiftFile) {
      toast.error("Please Enter Mobile Number or Upload File");
      return;
    }
    if (!selectedMetal) {
      toast.error("Please select a metal.");
      return;
    }
    if (!giftValidity) {
      toast.error("Please Enter Gift Validity");
      return;
    }
    if (!occasion) {
      toast.error("Please Enter Occasion");
      return;
    }
    if (!bulkMetalGiftFile && !weight) {
      toast.error("Please enter weight or upload file");
      return;
    }

    const invalidNumbers = mobileList.filter(
      (m) => m.length < MIN_PHONE_LENGTH || m.length > MAX_PHONE_LENGTH
    );
    if (invalidNumbers.length > 0) {
      toast.error(
        `Mobile numbers must be between ${MIN_PHONE_LENGTH} and ${MAX_PHONE_LENGTH} digits`
      );
      return;
    }

    let sendtype;
    if (bulkMetalGiftFile) {
      sendtype = "file";
    } else {
      sendtype = "phone";
    }
    setIsLoading(true);

    dispatch(
      bulkGiftRequest({
        sendtype: sendtype,
        phonelist: JSON.stringify(mobileList),
        weight,
        metalName: selectedMetal.metalName,
        purity: selectedMetal.purity,
        occasion,
        giftvalidity: Number(giftValidity),
        bulkMetalGiftFile,
      })
    );

    setTimeout(() => {
      setIsLoading(false);
      setSelectedMetal(null);
      setOccasion("");
      setGiftValidity("");
      setWeight("");
      setMobile("");
      setMobileList([]);
      setBulkMetalGiftFile(null);
    }, 2000);
  };

  if (configData?.featureToggle?.bulk_gift !== true) {
    return <FeatureUnavailable heading="Bulk Gift" />;
  }

  return (
    <Box sx={{ width: "100%", mt: { xs: 10, md: 10 }, mb: { xs: 2, md: 2 } }}>
      <Paper
        elevation={2}
        sx={{ p: 3, width: "100%", border: "1px solid #ccc" }}
      >
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={"Aeonik"}>
            Bulk Gift
          </Typography>
          <Button
            component="a"
            href="https://api.instalaxmi.co/static_data/Bulk gift.xlsx"
            download
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<CloudDownloadIcon />}
            sx={{ color: "#752ADA", textTransform: "none", fontSize: '15px' }}
          >
            Download excel
          </Button>
        </Grid>

        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" sx={{ width: '142px' }}>
              <InputLabel>
                Select Metal <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <Select
                value={selectedMetal ? selectedMetal._id : ""}
                label="Select Metal"
                onChange={(e) => {
                  const selected = metalsdata.find(
                    (m) => m._id === e.target.value
                  );
                  setSelectedMetal(selected);
                }}
              >
                {(metalsdata || [])
                  .filter((metal) => metal.status === true)
                  .map((metalItem) => (
                    <MenuItem key={metalItem._id} value={metalItem._id}>
                      {metalItem.metalName} ({metalItem.karat}k -{" "}
                      {metalItem.purity})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Occasion"
              value={occasion}
              onChange={(e) => {
                const value = e.target.value;
                const alphabetOnly = value.replace(/[^a-zA-Z\s]/g, "");
                const noLeadingSpace = alphabetOnly.replace(/^\s+/, "");
                setOccasion(noLeadingSpace);
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Validity Days"
              value={giftValidity}
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/\D/g, "");
                value = value.replace(/^0+/, "");
                setGiftValidity(value);
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Gift Weight (Gms) Per Person"
              value={weight}
              disabled={!!bulkMetalGiftFile}
              onChange={(e) => {
                let value = e.target.value;
                if (/^\d*\.?\d{0,4}$/.test(value)) {
                  setWeight(value);
                  if (value) {
                    setBulkMetalGiftFile(null);
                  }
                }
              }}
              fullWidth
              inputProps={{
                inputMode: "decimal",
                pattern: "^[0-9]*\\.?[0-9]+$",
              }}
            />
          </Grid>

         
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Typography fontWeight={500} mb={1}>
              Customer List<span style={{ color: "red" }}>*</span>
            </Typography>
            <input
              type="file"
              disabled={!!weight}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setBulkMetalGiftFile(file);
                  setWeight("");
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" color="text.secondary">
              OR
            </Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              size="small"
              label="Enter Mobile Number"
              value={mobile}
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/\D/g, "");
                if (value.length > 13) value = value.slice(0, 13);
                setMobile(value);
              }}
              fullWidth
              inputProps={{ minLength: 5, maxLength: 13 }}
            />
          </Grid>

          <Grid item xs={12} sm={4} sx={{ pr: 50 }}>
            <Typography fontWeight={500}>Add</Typography>
            <IconButton
              onClick={handleAddMobile}
              disabled={!mobile || mobileList.includes(mobile)}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Grid>
          
          <Grid item xs={12}>
            <List dense>
              {mobileList.map((num, idx) => (
                <ListItem
                  key={idx}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveMobile(num)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={num} />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid contaner spacing={2}>
              <Grid item xs={12} sx={{flexBasis: "100%", maxWidth: "100%"}}>
            {(canCreateSetting || canEdit) && (
              <Button
                variant="contained"
                sx={{ backgroundColor: "#752ADA" }}
                onClick={handleBulkGift}
              >
                {isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Upload & Gift"
                )}
              </Button>
            )}
          </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default BulkGifts;
